/**
 * Refinance Calculator
 *
 * DEBUG URL
 * https://coastal1.test/refinance-calculator/?logged-out&initial-value=2500&zip-code=02030&lead-name=Kevin Leary&lead-email=coastal1@kevinleary.net&lead-phone=7815901000&lead-car-loans=500&lead-student-loans=1000&lead-credit-cards=75&lead-personal-loans=250
 */
import tippy from "tippy.js";
import { numberToCurrency, markFieldAsValid, markFieldAsInvalid, trackEvent, numberFormat, findPropertyTaxForZipCode, dollarConfig, percentageConfig, customDropdowns, PMT, PV, FV, CUMIPMT, usd } from "./_calculators";
import { isEqual, each, includes, toNumber, some, isEmpty, round, random } from "lodash-es";
import dayjs from "dayjs/esm/index.js";
import "../vendor/select2.min";
import "./_serialize-object";

class CalculatorsRefinance {
  // Tracks error count
  errors = 0;
  totalPayment = 0;
  totalMonthlyPayment = 0;
  lastCalculation = {};

  // Initialize
  constructor() {
    customDropdowns();
    this.createOdometers();
    this.defineInputs();
    this.formHandler();
    this.initialState();
    this.adjustCheckboxLabels();
    this.tooltips();
  }

  formatDate(date) {
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    let month = "" + (dateObj.getMonth() + 1);

    if (month.length < 2) {
      month = "0" + month;
    }

    return [year, month].join("-");
  }

  addYears(date, years) {
    const day = 24 * 60 * 60 * 1000;
    const year = day * 365;
    const newdate = new Date(Date.now() + years * year);
  }

  // Tooltips
  tooltips() {
    tippy("[data-tippy-content]");
  }

  // Advanced number inputs
  defineInputs() {
    // Calc inputs
    this.originationDate = $("input#origination-date");
    this.initialValue = new AutoNumeric("#initial-value", dollarConfig);
    this.interestRate = new AutoNumeric("#interest-rate", percentageConfig);
    this.loanTerm = $("#loan-term");
    this.refinanceDate = $("input#refinance-date");
    this.refinanceRate = new AutoNumeric("#refinance-rate", percentageConfig);
    this.refinanceTerm = $("#refinance-term");
    this.applySavingsToPrinciple = $("input#apply-savings-to-principal");

    // Dynamic attributes
    const now = dayjs().format("YYYY-MM");
    const future = dayjs().add(30, "years").format("YYYY-MM");
    this.originationDate.prop("max", now);
    this.refinanceDate.prop("min", now);
    this.refinanceDate.prop("max", future);

    // Events
    this.loanTerm.on("change", (e) => {
      this.mortgageTermChanged(e);
    });
  }

  // Mortgage term changed, adjust min/max for dates
  mortgageTermChanged(e) {
    const years = toNumber(this.loanTerm.val());
    const min = dayjs().subtract(years, "years").format("YYYY-MM");

    this.loanTerm.prop("min", min);
  }

  // Fade-in onload
  initialState() {
    $(() => {
      $(".calculator").addClass("ready");
    });
  }

  // Adjust date inputs
  adjustDateInputs() {
    $('form[data-calculator="refinance"]').on("focus", "input[type=month]", function (e) {
      $(this).on("wheel.disableScroll", function (e) {
        e.preventDefault();
      });
    });

    $('form[data-calculator="refinance"]').on("blur", "input[type=month]", function (e) {
      $(this).off("wheel.disableScroll");
    });
  }

  // Pretty checkbox change label adjust
  adjustCheckboxLabels() {
    const $checkbox = $("#apply-savings-to-principal");

    $checkbox.on("change", (e) => {
      if ($(this).is("checked")) {
        console.log("checked");
      } else {
        console.log("unchecked");
      }
    });
  }

  // Calulator form validation
  formIsValid() {
    // Input values
    let originationDate = this.originationDate.val();
    let initialValue = this.initialValue.rawValue;
    let interestRate = this.interestRate.rawValue;
    let loanTerm = this.loanTerm.val();
    let refinanceDate = this.refinanceDate.val();
    let refinanceRate = this.refinanceRate.rawValue;
    let refinanceTerm = this.refinanceTerm.val();

    // Required fields are empty
    if (some([originationDate, initialValue, loanTerm, refinanceTerm], isEmpty)) return false;

    // Invalid number
    if (initialValue <= 0) return false;
    if (interestRate <= 0) return false;
    if (refinanceRate <= 0) return false;

    return true;
  }

  // Odometers
  createOdometers() {
    this.odometer = {};

    this.odometer.existingInterest = new Odometer({
      el: $('[data-result="existing-interest"]').get(0),
      value: 0,
      animation: "count",
      format: "(,ddd)",
      theme: "default",
    });

    this.odometer.newInterest = new Odometer({
      el: $('[data-result="new-interest"]').get(0),
      value: 0,
      animation: "count",
      format: "(,ddd)",
      theme: "default",
    });

    this.odometer.interestSavings = new Odometer({
      el: $('[data-result="interest-savings"]').get(0),
      value: 0,
      animation: "count",
      format: "(,ddd)",
      theme: "default",
    });

    this.odometer.existingPayment = new Odometer({
      el: $('[data-result="existing-monthly-payment"]').get(0),
      value: 0,
      animation: "count",
      format: "(,ddd)",
      theme: "default",
    });

    this.odometer.newMonthlyPayment = new Odometer({
      el: $('[data-result="new-monthly-payment"]').get(0),
      value: 0,
      animation: "count",
      format: "(,ddd)",
      theme: "default",
    });

    this.odometer.monthlySavings = new Odometer({
      el: $('[data-result="monthly-savings"]').get(0),
      value: 0,
      animation: "count",
      format: "(,ddd)",
      theme: "default",
    });
  }

  // Display output charts and figures
  async displayOutput() {
    // Calculate results
    let input = {
      originationDate: this.originationDate.val(),
      initialValue: this.initialValue.rawValue,
      interestRate: this.interestRate.rawValue,
      loanTerm: this.loanTerm.val(),
      refinanceDate: this.refinanceDate.val(),
      refinanceRate: this.refinanceRate.rawValue,
      refinanceTerm: this.refinanceTerm.val(),
      applySavingsToPrinciple: this.applySavingsToPrinciple.is(":checked"),
    };

    // Convert numbers to int
    const numbers = ["initialValue", "interestRate", "loanTerm", "refinanceRate", "refinanceTerm"];
    each(input, (val, key) => {
      if (includes(numbers, key)) {
        input[key] = toNumber(val);
      }
    });

    // Payments made based on origination and refinance dates
    const originatedOn = dayjs(input.originationDate);
    const refinancedOn = dayjs(input.refinanceDate);
    const numberOfPaymentsMade = refinancedOn.diff(originatedOn, "months", true);
    const calculation = this.refinanceMortgage(input.initialValue, input.interestRate, input.loanTerm, input.refinanceRate, input.refinanceTerm, numberOfPaymentsMade);

    // Test for duplicate
    if (isEqual(this.lastCalculation, calculation)) return;

    // Track analytics event: category, action, label
    const eventAction = "$" + numberFormat(round(input.initialValue, 2));
    const eventValue = `${input.interestRate}%`;
    const eventLabel = `${input.refinanceRate}%`;
    trackEvent("Refinance Calculator Submitted", eventAction, eventLabel, eventValue);

    // Store as last submission
    this.lastCalculation = calculation;

    // Update display values
    this.odometer.existingInterest.update(calculation.existingTotalInterest);
    this.odometer.existingPayment.update(calculation.existingMonthlyMortgagePayment);
    this.odometer.newMonthlyPayment.update(calculation.newMonthlyMortgagePayment);

    // Pos/neg values
    this.updatePosNegValue(this.odometer.newInterest, calculation.newTotalInterest, "new-interest");

    this.updatePosNegValue(this.odometer.interestSavings, calculation.totalInterestSaved, "interest-savings");

    this.updatePosNegValue(this.odometer.newMonthlyPayment, calculation.newMonthlyMortgagePayment, "new-monthly-payment");

    const monthlySavings = calculation.existingMonthlyMortgagePayment - calculation.newMonthlyMortgagePayment;

    this.updatePosNegValue(this.odometer.monthlySavings, monthlySavings, "monthly-savings");

    const refinanceValue = usd(calculation.newMortgageTotal);
    $("#refinance-value").hide().text(refinanceValue).fadeIn(500);
  }

  // Update <td> and flag as positive or negative
  updatePosNegValue(odometer, value, id) {
    // Classes
    const $td = $(`[data-result="${id}"]`).parent("td");
    $td.removeAttr("class");
    if (value < 0) {
      $td.addClass("negative-number");
    } else {
      $td.addClass("positive-number");
    }

    // Title text
    const $tr = $(`[data-result="${id}"]`).parents("tr");
    const $title = $("h3", $tr);
    const positiveTitleText = $title.data("positive-text");
    const negativeTitleText = $title.data("negative-text");
    if (negativeTitleText && positiveTitleText) {
      const text = value < 0 ? negativeTitleText : positiveTitleText;
      $title.hide().text(text).fadeIn(500);
    }

    const delay = random(0, 250);
    setTimeout(() => {
      odometer.update(Math.abs(value));
    }, delay);
  }

  // Form submit handler
  formHandler() {
    // Prevent default submit
    $('form[data-calculator="refinance"]').on("submit", (event) => {
      event.preventDefault();
    });

    // Submit on input changes
    $('form[data-calculator="refinance"] input, form[data-calculator="refinance"] select').on("change", (event) => {
      this.submitForm(event);
    });

    // Submit onload
    if (this.formIsValid()) {
      this.displayOutput();
    }
  }

  // Submit calculator form
  submitForm = (event) => {
    const element = $(event.delegateTarget);
    const id = element.attr("id");
    let value;

    // #origination-date
    if (id === "origination-date") {
      value = this.originationDate.val();
      if (!value || value <= 0) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }
    }

    // #initial-value
    if (id === "initial-value") {
      value = this.initialValue.rawValue;
      if (!value || value <= 0) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }
    }

    // #interest-rate
    if (id === "interest-rate") {
      value = this.interestRate.rawValue;
      if (!value || value <= 0) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }
    }

    // #loan-term
    if (id === "loan-term") {
      value = this.loanTerm.val();
      if (!value || toNumber(value) <= 0) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }
    }

    // #refinance-date
    if (id === "refinance-date") {
      value = this.refinanceDate.val();
      if (!value || value <= 0) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }
    }

    // #refinance-rate
    if (id === "refinance-rate") {
      value = this.refinanceRate.rawValue;
      if (!value || value <= 0) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }
    }

    // #refinance-term
    if (id === "refinance-term") {
      value = this.refinanceTerm.val();
      if (!value || toNumber(value) <= 0) {
        markFieldAsInvalid(element);
        return;
      } else {
        markFieldAsValid(element);
      }
    }

    // Verify required fields
    if (!this.formIsValid()) return;

    // Display output report
    this.displayOutput();
  };

  calculateMonthlyMortgagePayment(loanAmount, interestRate, termInYears) {
    var principal = loanAmount;
    var interestRate = interestRate == 0 ? 0 : interestRate / 100;
    var monthlyInterestRate = interestRate == 0 ? 0 : interestRate / 12;
    var numberOfMonthlyPayments = termInYears * 12;

    return (monthlyInterestRate * principal * Math.pow(1 + monthlyInterestRate, numberOfMonthlyPayments)) / (Math.pow(1 + monthlyInterestRate, numberOfMonthlyPayments) - 1);
  }

  calculateMonthlyInterestPayment(monthlyInterestRate, remainingBalance) {
    return monthlyInterestRate * remainingBalance;
  }

  refinanceMortgage(loanAmount, interestRate, loanTerm, refinanceRate, refinanceTerm, numberOfPaymentsMade) {
    // base monthly mortage payment
    var monthlyMortgagePayment = this.calculateMonthlyMortgagePayment(loanAmount, interestRate, loanTerm);
    var remainingBalance = loanAmount;
    var totalPaid = 0;
    var totalInterest = 0;
    for (var i = 0; i < numberOfPaymentsMade; i++) {
      // convert interest rate into a monthly percentage rate
      var monthlyInterestPayment = this.calculateMonthlyInterestPayment(interestRate / 100 / 12, remainingBalance);
      var monthlyPrincipalPayment = monthlyMortgagePayment - monthlyInterestPayment;

      // if the total monthly payment is no longer less than remaining balance, then we are at our last payment
      if (remainingBalance - monthlyPrincipalPayment >= 0) {
        remainingBalance -= monthlyPrincipalPayment;
      }
      totalPaid += monthlyPrincipalPayment;
      totalInterest += monthlyInterestPayment;
    }

    // calculate the remaining interest at the end of the number of payments made
    var remainingInterest = monthlyMortgagePayment * loanTerm * 12 - loanAmount - totalInterest;

    // the new mortgage total is the remaining balance
    var newMortgageTotal = remainingBalance;

    // lets calculate the new monthly mortgage payment with the new mortgage total
    var newMonthlyMortgagePayment = this.calculateMonthlyMortgagePayment(newMortgageTotal, refinanceRate, refinanceTerm);

    // calculate remaining total interest
    var newRemainingInterest = newMonthlyMortgagePayment * refinanceTerm * 12 - remainingBalance;

    // build out the response
    var response = {
      totalInterestSaved: remainingInterest - newRemainingInterest,
      existingMonthlyMortgagePayment: monthlyMortgagePayment,
      existingTotalInterest: remainingInterest,
      newMortgageTotal: newMortgageTotal,
      newMonthlyMortgagePayment: newMonthlyMortgagePayment,
      newTotalInterest: newRemainingInterest,
    };
    return response;
  }
}

new CalculatorsRefinance();
